/**
 * Pending Upload Manager
 * Handles persisting file upload state across OAuth redirects
 */

const PENDING_UPLOAD_KEY = 'av_pulse_pending_upload'

export interface PendingUpload {
  fileName: string
  fileType: string
  fileSize: number
  fileData: string // Base64 encoded file data
  detectedType: 'image' | 'video'
  timestamp: number
}

/**
 * Convert File to base64 string
 */
async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = error => reject(error)
  })
}

/**
 * Convert base64 string back to File
 */
function base64ToFile(
  base64: string,
  fileName: string,
  fileType: string,
): File {
  const arr = base64.split(',')
  const mime = arr[0].match(/:(.*?);/)?.[1] || fileType
  const bstr = atob(arr[1])
  let n = bstr.length
  const u8arr = new Uint8Array(n)

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n)
  }

  return new File([u8arr], fileName, { type: mime })
}

/**
 * Store a pending file upload in localStorage
 */
export async function storePendingUpload(
  file: File,
  detectedType: 'image' | 'video',
): Promise<void> {
  try {
    const fileData = await fileToBase64(file)

    const pendingUpload: PendingUpload = {
      fileName: file.name,
      fileType: file.type,
      fileSize: file.size,
      fileData,
      detectedType,
      timestamp: Date.now(),
    }

    localStorage.setItem(PENDING_UPLOAD_KEY, JSON.stringify(pendingUpload))
  } catch (error) {
    console.error('Failed to store pending upload:', error)
    throw new Error('Failed to store file for upload')
  }
}

/**
 * Retrieve a pending file upload from localStorage
 */
export function getPendingUpload(): {
  file: File
  detectedType: 'image' | 'video'
} | null {
  try {
    const stored = localStorage.getItem(PENDING_UPLOAD_KEY)

    if (!stored) {
      return null
    }

    const pendingUpload: PendingUpload = JSON.parse(stored)

    // Check if upload is not too old (max 1 hour)
    const ONE_HOUR = 60 * 60 * 1000
    if (Date.now() - pendingUpload.timestamp > ONE_HOUR) {
      clearPendingUpload()
      return null
    }

    const file = base64ToFile(
      pendingUpload.fileData,
      pendingUpload.fileName,
      pendingUpload.fileType,
    )

    return {
      file,
      detectedType: pendingUpload.detectedType,
    }
  } catch (error) {
    console.error('Failed to retrieve pending upload:', error)
    clearPendingUpload()
    return null
  }
}

/**
 * Check if there's a pending upload
 */
export function hasPendingUpload(): boolean {
  return localStorage.getItem(PENDING_UPLOAD_KEY) !== null
}

/**
 * Clear the pending upload from localStorage
 */
export function clearPendingUpload(): void {
  localStorage.removeItem(PENDING_UPLOAD_KEY)
}
